import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import * as fsmApi from "@/lib/api/financial-situation-models";
import { useInvalidateFlowCachesForFinancialModelEdit } from "./use-flow-cache-invalidation";

// --- Query hooks ---

export function useFinancialSituationModels() {
  const { data, error, isLoading } = useQuery({
    queryKey: ["financialSituationModels"],
    queryFn: fsmApi.list,
  });
  return { models: data ?? [], error, isLoading };
}

export function useFinancialSituationModel(id: string | null) {
  const { data, error, isLoading } = useQuery({
    queryKey: ["financialSituationModels", id],
    queryFn: () => fsmApi.getById(id!),
    enabled: !!id,
  });
  return { model: data ?? null, error, isLoading };
}

// --- Mutation hooks ---

export function useCreateFinancialSituationModel() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: fsmApi.create,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["financialSituationModels"] }),
  });
}

export function useUpdateFinancialSituationModel() {
  const queryClient = useQueryClient();
  const invalidateFlowCaches = useInvalidateFlowCachesForFinancialModelEdit();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Parameters<typeof fsmApi.update>[1] }) =>
      fsmApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["financialSituationModels"] });
      // Flows and instances embed the model (fields + settings) — see the helper.
      invalidateFlowCaches();
    },
  });
}

export function useDeleteFinancialSituationModel() {
  const queryClient = useQueryClient();
  const invalidateFlowCaches = useInvalidateFlowCachesForFinancialModelEdit();
  return useMutation({
    mutationFn: fsmApi.remove,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["financialSituationModels"] });
      invalidateFlowCaches();
    },
  });
}
